import { useCallback, useMemo } from 'react';

import { v4 as uuid } from 'uuid';

import { useReorderTemplate } from '../data/useReorderTemplate';
import type { ReorderChange } from '../model/changes';
import { buildReorderQuestions, buildReorderSections } from '../model/reorder';
import { renderedTemplate } from '../state/selectors';
import { LABEL_KEYS, type Message } from '../viewmodels/messages';
import { runOperation, type RunOperationDeps } from './runOperation';

export type ReorderActions = {
  reorderSections: (activeId: string, overId: string) => Promise<void>;
  reorderQuestions: (
    sectionId: string,
    activeId: string,
    overId: string
  ) => Promise<void>;
};

export function useReorderActions(deps: RunOperationDeps): ReorderActions {
  const { getLatestState } = deps;
  const reorder = useReorderTemplate();

  // Each drag gets its own key, so a drag made while another is saving is not refused
  // as a second operation on the same target. The lane writes them in the order started
  const startReorder = useCallback(
    async (change: ReorderChange, successMessage: Message) => {
      const { templateId, channelId } = getLatestState();

      await runOperation(
        {
          op: { kind: 'reorder', targetId: uuid(), change },
          execute: context =>
            reorder({ channelId, templateId, change }, context),
          successMessage,
        },
        deps
      );
    },
    [getLatestState, reorder, deps]
  );

  const reorderSections = useCallback(
    async (activeId: string, overId: string) => {
      const template = renderedTemplate(getLatestState());
      const change =
        template && buildReorderSections(template, { activeId, overId });

      if (change) {
        await startReorder(change, {
          key: LABEL_KEYS.reorderSectionSucceededToast,
        });
      }
    },
    [getLatestState, startReorder]
  );

  const reorderQuestions = useCallback(
    async (sectionId: string, activeId: string, overId: string) => {
      const template = renderedTemplate(getLatestState());
      const change =
        template &&
        buildReorderQuestions(template, { sectionId, activeId, overId });

      if (change) {
        await startReorder(change, {
          key: LABEL_KEYS.reorderQuestionSucceededToast,
        });
      }
    },
    [getLatestState, startReorder]
  );

  return useMemo(
    () => ({ reorderSections, reorderQuestions }),
    [reorderSections, reorderQuestions]
  );
}
